import {
  StyleSheet,
  View,
  ScrollView,
  FlatList,
  Image,
  ImageStyle,
  ViewStyle,
  TextStyle,
} from 'react-native';
import React, {useCallback, useEffect, useState} from 'react';
import {
  Text,
  SearchInput,
  Button,
  GradientButton,
  Pagination,
  SearchSelect,
} from 'ui-kits';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';
import colors from 'theme/Colors';
import {spacing} from 'theme/Units';
import {useSelector, useDispatch} from 'react-redux';
import {
  backlogList,
  userStoryStatus,
  userStoryFailure,
  userStorySuccess,
  backlogTotalPages,
} from 'redux/selectors/userStorySelector';
import {
  getBacklogsRequest,
  resetMessage,
  UserStory,
  ResponsibleEmployee,
  addUserStoryRequest,
} from 'redux/slices/userStorySlice';
import {
  selectedProjectID,
  selectedProjectTeam,
} from 'redux/selectors/projectSelector';
import {useAuthAccess, useDebounceState, useToast} from 'hooks';
import {SUCCESS_KEY, ERROR_KEY} from 'constants/keys';
import {
  ErrorCodes,
  SuccessCodes,
  getErrorMessage,
  getSuccessMessage,
} from 'utils/getMessages';
import {
  AddUserStoryDropDown,
  QuickUserStoryAddModal,
  UserStoryCard,
} from 'components/backlogs_screen';
import {projectTeamMember} from 'redux/slices/projectSlice';
import {getFormattedDate} from 'utils/getFormattedDate';

type quickAddStoryTypes = {
  title: string;
  due_date: Date | null;
  responsible: projectTeamMember[];
  priority: number;
};

export function ProjectBacklogs({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const dispatch = useDispatch();
  const backlogs = useSelector(backlogList);
  const totalPages = useSelector(backlogTotalPages);
  const backlogError = useSelector(userStoryFailure);
  const backlogSuccess = useSelector(userStorySuccess);
  const currentProjectID = useSelector(selectedProjectID);
  const projectTeam = useSelector(selectedProjectTeam);
  const {showToasts} = useToast();

  const [responsible, setResponsible] = useState<ResponsibleEmployee | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useDebounceState<string>('', 500);
  const [userStoryDropdownOpen, setUserStoryDropdownOpen] =
    useState<boolean>(false);
  const [quickAddModalOpen, setQuickAddModalOpen] = useState<boolean>(false);
  const {hasWriteAccess} = useAuthAccess();

  // fetch backlogs
  const fethBacklogs = useCallback(() => {
    if (currentProjectID) {
      const payload = {
        id: currentProjectID,
        filter: JSON.stringify([{responsible: responsible?.emp_id}]),
        page: currentPage,
        search_text: searchText!,
      };
      dispatch(getBacklogsRequest(payload));
    }
  }, [dispatch, currentProjectID, currentPage, searchText, responsible]);

  useEffect(() => {
    if (backlogError) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(backlogError as ErrorCodes),
        'error',
      );
      dispatch(resetMessage());
    }

    if (backlogSuccess) {
      if (backlogSuccess !== 'S-10023' && backlogSuccess !== 'S-10021') {
        showToasts(
          SUCCESS_KEY,
          getSuccessMessage(backlogSuccess as SuccessCodes),
          'success',
        );
      }
      if (backlogSuccess === 'S-10020') {
        fethBacklogs(); // fetch backloags after adding
      }
      dispatch(resetMessage());
    }
  }, [backlogError, backlogSuccess, dispatch, showToasts, fethBacklogs]);

  useEffect(() => {
    fethBacklogs();
  }, [fethBacklogs]);

  function renderResponsiblePlaceholder(item: projectTeamMember) {
    return (
      <Text>
        {item?.first_name} {item?.last_name}
      </Text>
    );
  }

  const handleGenerataUserStoryBeta = () => {};

  const handleQuickAddStory = (values: quickAddStoryTypes) => {
    const payload = Object.keys(values).reduce((acc: any, item: string) => {
      const key = item as keyof quickAddStoryTypes;
      if (values[key]) {
        acc[key] = values[key];
      }
      return acc;
    }, {});
    dispatch(
      addUserStoryRequest({
        ...payload,
        due_date: values.due_date
          ? getFormattedDate(values.due_date, 'yyyy-mm-dd', '-')
          : null,
        responsible: values.responsible[0]?.emp_id || null,
        proj_id: currentProjectID,
        status: 1,
      }),
    );
    setQuickAddModalOpen(false);
  };

  function handleAddStoryWithDetial() {
    navigation.navigate(Routes.ADD_USER_STORY, {
      id: currentProjectID?.toString()!,
    });
  }

  return (
    <View style={styles.container}>
      {/* header */}
      <View style={styles.header}>
        <View style={styles.searchInput}>
          <SearchInput
            placeholder="Search Story by Title Or id"
            onChangeText={setSearchText}
          />
        </View>
        <View style={styles.searchInput}>
          <SearchSelect
            onSave={values => setResponsible(values[0])}
            searchKeyExtractor={(item: projectTeamMember) =>
              item.first_name + item.last_name
            }
            multiple={false}
            options={projectTeam}
            selectedEntries={responsible ? [responsible] : []}
            keyExtractor={(item: projectTeamMember) => item.id.toString()}
            renderItem={(item: projectTeamMember) => (
              <Text>
                {item.first_name} {item.last_name}
              </Text>
            )}
            displayItem={renderResponsiblePlaceholder}
            searchPlaceholder="Search Responsible"
            placeholder="Search Employee"
          />
        </View>
        {hasWriteAccess ? (
          <>
            <GradientButton
              title="Generate User Story"
              isBeta
              onPress={handleGenerataUserStoryBeta}
              style={styles.gradientButton}
              icon={
                <Image
                  resizeMode="contain"
                  style={styles.icon}
                  source={require('assets/icons/stars_white@2x.png')}
                />
              }
            />
            <View style={styles.dropdownContainer}>
              <Button
                onPress={() => setUserStoryDropdownOpen(p => !p)}
                style={styles.button}
                text="Add User Story +"
              />
              {userStoryDropdownOpen ? (
                <AddUserStoryDropDown
                  onAddWithDetails={() => {
                    setUserStoryDropdownOpen(false);
                    handleAddStoryWithDetial();
                  }}
                  onQuickAdd={() => {
                    setUserStoryDropdownOpen(false);
                    setQuickAddModalOpen(true);
                  }}
                />
              ) : null}
            </View>
          </>
        ) : null}
      </View>

      {/* content */}
      <DisplayContent navigate={navigation.navigate} backlogs={backlogs} />

      {/* pagination text  */}
      {totalPages !== 1 && (
        <Text style={styles.pageNumber}>
          Showing {backlogs.length} of {totalPages * 10} results
        </Text>
      )}

      {totalPages !== 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageHandler={setCurrentPage}
        />
      )}

      <QuickUserStoryAddModal
        visible={quickAddModalOpen}
        onCancel={() => setQuickAddModalOpen(false)}
        onSave={handleQuickAddStory}
      />
    </View>
  );
}

type propType = Readonly<{
  backlogs: any[];
  navigate: (route: string, params: any) => void;
}>;
function DisplayContent(props: propType) {
  const {backlogs, navigate} = props;
  const isLoading = useSelector(userStoryStatus);
  if (isLoading) {
    return (
      <View style={styles.loading}>
        <Text>Please wait while we load your data</Text>
      </View>
    );
  }
  return (
    <ScrollView>
      <FlatList
        data={backlogs}
        keyExtractor={(item: UserStory) => item.id.toString()}
        renderItem={({item}) => (
          <UserStoryCard data={item} navigate={navigate} />
        )}
        contentContainerStyle={styles.list}
      />
    </ScrollView>
  );
}

type styleType = {
  icon: ImageStyle;
  loading: ViewStyle;
  list: ViewStyle;
  container: ViewStyle;
  header: ViewStyle;
  searchInput: ViewStyle;
  gradientButton: ViewStyle;
  button: ViewStyle;
  dropdownContainer: ViewStyle;
  pageNumber: TextStyle;
};
const styles: styleType = StyleSheet.create({
  pageNumber: {
    fontSize: 14,
    marginTop: spacing['2'],
    zIndex: -1,
  },
  icon: {
    width: 20,
    height: 20,
  },
  loading: {
    flex: 1,
  },
  list: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing['2'],
  },
  container: {
    backgroundColor: colors.palette.white,
    flex: 1,
    padding: spacing['4'],
    gap: spacing['2'],
  },
  header: {
    flexDirection: 'row',
    gap: spacing['2'],
    zIndex: 10,
  },
  searchInput: {
    flex: 1,
  },
  gradientButton: {
    marginLeft: 'auto',
  },
  button: {
    width: 'fit-content',
    paddingHorizontal: spacing['4'],
  },
  dropdownContainer: {
    position: 'relative',
  },
});
